import React, { useEffect, useState, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "../api";
import { ArrowLeft, Plus, Check, Trash2, Save, Github, Globe } from "lucide-react";

const STAGE_STATUSES = ["pending", "active", "done"];

const ProjectDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [client, setClient] = useState(null);
  const [meta, setMeta] = useState({});
  const [dirty, setDirty] = useState(false);
  const [savingMeta, setSavingMeta] = useState(false);
  const taskInputs = useRef({});

  const load = async () => {
    const { data } = await api.get(`/projects/${id}`);
    setProject(data);
    setMeta({
      name: data.name || "",
      app_type: data.app_type || "",
      contract_value: data.contract_value ?? "",
      deposit_paid: data.deposit_paid ?? "",
      target_launch_date: data.target_launch_date || "",
      repo_url: data.repo_url || "",
      live_url: data.live_url || "",
      notes: data.notes || "",
      status: data.status || "active",
    });
    setDirty(false);
    if (data.client_id) {
      try {
        const c = await api.get(`/clients/${data.client_id}`);
        setClient(c.data);
      } catch (_) {}
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const onMeta = (k, v) => {
    setMeta((m) => ({ ...m, [k]: v }));
    setDirty(true);
  };

  const saveMeta = async () => {
    setSavingMeta(true);
    try {
      const payload = {
        name: meta.name.trim() || null,
        app_type: meta.app_type.trim() || null,
        contract_value: meta.contract_value === "" ? null : Number(meta.contract_value),
        deposit_paid: meta.deposit_paid === "" ? null : Number(meta.deposit_paid),
        target_launch_date: meta.target_launch_date || null,
        repo_url: meta.repo_url.trim() || null,
        live_url: meta.live_url.trim() || null,
        notes: meta.notes,
        status: meta.status,
      };
      const { data } = await api.patch(`/projects/${id}`, payload);
      setProject(data);
      setDirty(false);
    } finally {
      setSavingMeta(false);
    }
  };

  const setStageStatus = async (stageId, status) => {
    const { data } = await api.patch(`/projects/${id}/stages/${stageId}`, { status });
    setProject(data);
  };

  const addTask = async (stageId, title) => {
    if (!title.trim()) return;
    const { data } = await api.post(`/projects/${id}/stages/${stageId}/tasks`, { title: title.trim() });
    setProject(data);
    if (taskInputs.current[stageId]) taskInputs.current[stageId].value = "";
  };

  const toggleTask = async (stageId, taskId, done) => {
    const { data } = await api.patch(`/projects/${id}/stages/${stageId}/tasks/${taskId}`, { done });
    setProject(data);
  };

  const deleteTask = async (stageId, taskId) => {
    const { data } = await api.delete(`/projects/${id}/stages/${stageId}/tasks/${taskId}`);
    setProject(data);
  };

  const deleteProject = async () => {
    if (!window.confirm("Delete this project permanently? This can't be undone.")) return;
    await api.delete(`/projects/${id}`);
    navigate(client ? `/admin/clients/${client.id}` : "/admin/projects");
  };

  if (!project) return <p className="text-[#9c95a8]">Loading…</p>;

  const totalTasks = (project.stages || []).reduce((a, s) => a + (s.tasks?.length || 0), 0);
  const doneTasks = (project.stages || []).reduce((a, s) => a + (s.tasks?.filter((t) => t.done).length || 0), 0);
  const pct = totalTasks ? Math.round((doneTasks / totalTasks) * 100) : 0;

  return (
    <div data-testid="project-detail-page">
      <div className="flex items-center justify-between gap-3 mb-6">
        <Link
          to={client ? `/admin/clients/${client.id}` : "/admin/projects"}
          className="inline-flex items-center gap-2 text-sm text-[#bdb6c4] hover:text-white"
        >
          <ArrowLeft size={14} /> Back to {client ? client.name : "projects"}
        </Link>
        <button
          onClick={deleteProject}
          className="inline-flex items-center gap-1.5 text-xs text-[#9c95a8] hover:text-red-300 transition-colors"
        >
          <Trash2 size={12} /> Delete project
        </button>
      </div>

      {/* Header */}
      <div className="bg-[#13121f] border border-white/10 rounded-2xl p-6 sm:p-8">
        <input
          value={meta.name}
          onChange={(e) => onMeta("name", e.target.value)}
          data-testid="project-name"
          className="font-display text-3xl sm:text-4xl font-bold tracking-tight bg-transparent border-none outline-none w-full focus:bg-white/[0.02] rounded px-1 -mx-1"
        />
        <div className="text-sm text-[#bdb6c4] mt-2">
          {client && (
            <Link to={`/admin/clients/${client.id}`} className="hover:text-[#f5c76e]">
              {client.name}
            </Link>
          )}
        </div>

        {/* Progress bar */}
        <div className="mt-6">
          <div className="flex justify-between text-xs text-[#9c95a8] mb-2 font-mono uppercase tracking-[0.18em]">
            <span>Progress</span>
            <span>{doneTasks}/{totalTasks} tasks · {pct}%</span>
          </div>
          <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
            <div
              className="h-full bg-[#f5c76e] rounded-full transition-all duration-500"
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>

        {/* Meta grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
          <Field label="App type">
            <input value={meta.app_type} onChange={(e) => onMeta("app_type", e.target.value)} data-testid="meta-app-type" className={fieldCls} />
          </Field>
          <Field label="Status">
            <select value={meta.status} onChange={(e) => onMeta("status", e.target.value)} data-testid="meta-status" className={`${fieldCls} cursor-pointer`}>
              <option value="active">Active</option>
              <option value="paused">Paused</option>
              <option value="launched">Launched</option>
              <option value="archived">Archived</option>
            </select>
          </Field>
          <Field label="Target launch">
            <input type="date" value={meta.target_launch_date || ""} onChange={(e) => onMeta("target_launch_date", e.target.value)} data-testid="meta-launch-date" className={fieldCls} />
          </Field>
          <Field label="Contract value ($)">
            <input type="number" step="0.01" value={meta.contract_value} onChange={(e) => onMeta("contract_value", e.target.value)} data-testid="meta-contract" className={fieldCls} placeholder="5000" />
          </Field>
          <Field label="Deposit paid ($)">
            <input type="number" step="0.01" value={meta.deposit_paid} onChange={(e) => onMeta("deposit_paid", e.target.value)} data-testid="meta-deposit" className={fieldCls} placeholder="2500" />
          </Field>
          <Field label="Outstanding">
            <p className="px-3 py-2.5 text-[#f5c76e] font-medium">
              ${Math.max((Number(meta.contract_value) || 0) - (Number(meta.deposit_paid) || 0), 0).toLocaleString()}
            </p>
          </Field>
          <Field label="Repo URL" wide>
            <div className="flex items-center gap-2">
              <Github size={14} className="text-[#9c95a8] shrink-0" />
              <input value={meta.repo_url} onChange={(e) => onMeta("repo_url", e.target.value)} data-testid="meta-repo" placeholder="https://github.com/…" className={fieldCls} />
            </div>
          </Field>
          <Field label="Live URL" wide>
            <div className="flex items-center gap-2">
              <Globe size={14} className="text-[#9c95a8] shrink-0" />
              <input value={meta.live_url} onChange={(e) => onMeta("live_url", e.target.value)} data-testid="meta-live" placeholder="https://…" className={fieldCls} />
            </div>
          </Field>
        </div>

        <Field label="Project notes" wide>
          <textarea
            rows={3}
            value={meta.notes}
            onChange={(e) => onMeta("notes", e.target.value)}
            data-testid="meta-notes"
            className={`${fieldCls} resize-none`}
            placeholder="Anything important to remember about this build…"
          />
        </Field>

        <div className="mt-4 flex items-center gap-3">
          <button
            onClick={saveMeta}
            disabled={!dirty || savingMeta}
            data-testid="save-meta-button"
            className={`inline-flex items-center gap-2 px-5 py-2 rounded-full font-semibold text-sm transition-colors ${
              dirty
                ? "bg-[#f5c76e] text-[#0a0a14] hover:bg-[#ffd485]"
                : "bg-white/5 text-[#6b6677] cursor-default"
            } disabled:opacity-60`}
          >
            <Save size={14} /> {savingMeta ? "Saving…" : dirty ? "Save changes" : "All saved"}
          </button>
        </div>
      </div>

      {/* Stages */}
      <div className="mt-8 space-y-4">
        <h2 className="font-display text-2xl font-bold">Build progress</h2>
        {project.stages.map((stage, idx) => {
          const stageDone = stage.tasks?.filter((t) => t.done).length || 0;
          const stageTotal = stage.tasks?.length || 0;
          return (
            <div
              key={stage.id}
              data-testid={`stage-${stage.name.toLowerCase()}`}
              className={`bg-[#13121f] border rounded-2xl p-5 sm:p-6 transition-colors ${
                stage.status === "active" ? "border-[#f5c76e]/40" :
                stage.status === "done" ? "border-emerald-500/30" :
                "border-white/10"
              }`}
            >
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3">
                  <span className="font-mono text-xs text-[#6b6677]">{String(idx + 1).padStart(2, "0")}</span>
                  <h3 className="font-display text-xl font-bold">{stage.name}</h3>
                  <span className="text-xs text-[#9c95a8]">· {stageDone}/{stageTotal}</span>
                </div>
                <div className="flex gap-2">
                  {STAGE_STATUSES.map((s) => (
                    <button
                      key={s}
                      onClick={() => setStageStatus(stage.id, s)}
                      data-testid={`stage-status-${stage.name.toLowerCase()}-${s}`}
                      className={`px-2.5 py-1 rounded-full text-[10px] font-mono uppercase tracking-[0.18em] border transition-colors ${
                        stage.status === s
                          ? s === "active" ? "bg-[#f5c76e] text-[#0a0a14] border-[#f5c76e]" :
                            s === "done" ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/40" :
                            "bg-white/10 text-[#bdb6c4] border-white/20"
                          : "border-white/10 text-[#9c95a8] hover:bg-white/[0.04]"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              {/* Tasks */}
              <ul className="mt-4 space-y-1.5">
                {(stage.tasks || []).map((t) => (
                  <li key={t.id} className="flex items-center gap-3 group">
                    <button
                      onClick={() => toggleTask(stage.id, t.id, !t.done)}
                      data-testid={`task-toggle-${t.id}`}
                      className={`w-5 h-5 rounded border flex items-center justify-center shrink-0 transition-colors ${
                        t.done
                          ? "bg-[#f5c76e] border-[#f5c76e] text-[#0a0a14]"
                          : "border-white/20 hover:border-[#f5c76e]/50"
                      }`}
                    >
                      {t.done && <Check size={12} strokeWidth={3} />}
                    </button>
                    <span className={`flex-1 text-sm ${t.done ? "line-through text-[#6b6677]" : "text-[#f4ede1]"}`}>
                      {t.title}
                    </span>
                    <button
                      onClick={() => deleteTask(stage.id, t.id)}
                      className="opacity-0 group-hover:opacity-100 text-[#6b6677] hover:text-red-300 transition-all"
                      title="Delete task"
                    >
                      <Trash2 size={12} />
                    </button>
                  </li>
                ))}
              </ul>

              {/* Add task */}
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  addTask(stage.id, e.currentTarget.querySelector("input").value);
                }}
                className="mt-3 flex items-center gap-2"
              >
                <Plus size={14} className="text-[#6b6677] shrink-0" />
                <input
                  ref={(el) => (taskInputs.current[stage.id] = el)}
                  data-testid={`add-task-${stage.name.toLowerCase()}`}
                  placeholder="Add a task…"
                  className="flex-1 bg-transparent border-none outline-none text-sm text-[#f4ede1] placeholder:text-[#6b6677] py-1.5"
                />
              </form>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const fieldCls =
  "w-full bg-[#0a0a14] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-[#f4ede1] outline-none focus:border-[#f5c76e]/60 transition-colors";

const Field = ({ label, children, wide }) => (
  <div className={wide ? "sm:col-span-2 lg:col-span-3 mt-2" : ""}>
    <label className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#9c95a8] block mb-2">{label}</label>
    {children}
  </div>
);

export default ProjectDetail;
